import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { SHORTCUTS } from '@/shared/hooks/useGlobalShortcuts';

/**
 * La chuleta de atajos, con `?`.
 *
 * Existe porque un atajo que nadie descubre no sirve de nada, y la única pista
 * que había —la del atajo de buscar, en la lateral— sólo cuenta uno de los
 * siete. (Esa pista ya no está escrita a mano: sale de `ATAJO_DE_BUSCAR`, que
 * es también la que se pinta aquí, porque `⌘` sólo existe en teclados de
 * Apple — #58.) Se lista la
 * misma constante que implementa los atajos, no una copia: una chuleta que
 * miente es peor que no tenerla.
 */
export function ShortcutsHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('shortcuts.title', { defaultValue: 'Atajos de teclado' })}
        className="w-full max-w-sm rounded-lg bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-serif text-base text-brand">
            {t('shortcuts.title', { defaultValue: 'Atajos de teclado' })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-muted hover:text-brand"
          >
            <X size={18} />
          </button>
        </div>

        <ul className="space-y-1.5">
          {SHORTCUTS.map((s) => (
            <li key={s.keys} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-ink">{t(s.labelKey, { defaultValue: s.labelDefault })}</span>
              <kbd className="text-muted shrink-0 rounded border border-neutral-200 bg-background px-1.5 py-0.5 font-mono text-[11px]">
                {s.keys}
              </kbd>
            </li>
          ))}
        </ul>

        <p className="text-muted mt-3 text-[11px]">
          {t('shortcuts.hint', {
            defaultValue: 'No funcionan mientras escribes en un campo.',
          })}
        </p>
      </div>
    </div>
  );
}
